import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useHomepageConfig } from '@/hooks/useHomepageConfig';
import { Editable } from '@/components/edit/Editable';

const API = process.env.REACT_APP_BACKEND_URL;

export const HomePerformances = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const cfg = useHomepageConfig();
  const s = cfg?.performancesSection || {};

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/performances`);
        if (!cancelled) {
          setItems(Array.isArray(res.data) ? res.data.sort((a, b) => a.number - b.number) : []);
        }
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const total = items.length;
  const next = useCallback(() => setIndex(i => (total ? (i + 1) % total : 0)), [total]);
  const prev = useCallback(() => setIndex(i => (total ? (i - 1 + total) % total : 0)), [total]);

  // Auto-advance every 6s
  useEffect(() => {
    if (!total) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, total]);

  const current = items[index];

  return (
    <section
      className="py-16 md:py-20 px-6 relative bg-black/30 backdrop-blur-md border-y border-white/10"
      data-testid="home-performances"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section title */}
        <div className="text-center mb-2">
          <Editable
            as="h2"
            path="performancesSection.sectionTitle"
            fallback={s.sectionTitle || 'Types of Performances and Recommendations'}
            className="text-2xl md:text-4xl font-black uppercase tracking-wide"
            style={{ color: '#F4E400' }}
          />
          <Editable
            as="p"
            path="performancesSection.sectionSubtitle"
            fallback={s.sectionSubtitle || 'A quick overview of our performances'}
            className="mt-3 text-xs md:text-sm font-bold uppercase tracking-widest"
            style={{ color: '#F4E400' }}
          />
        </div>

        {/* Divider */}
        <div className="my-8 h-px w-full" style={{ background: 'rgba(244,228,0,0.4)' }} />

        {/* CULTURA / ABOUT OUR SERVICES */}
        <div className="text-center mb-8">
          <Editable
            as="h3"
            path="performancesSection.culturaHeading"
            fallback={s.culturaHeading || 'CULTURA'}
            className="text-4xl md:text-6xl font-black tracking-widest"
            style={{
              backgroundImage: 'linear-gradient(180deg, #b98a1b 0%, #f0c34a 45%, #7a5b12 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontFamily: '"Times New Roman", serif',
              letterSpacing: '0.15em',
            }}
          />
          <Editable
            as="p"
            path="performancesSection.culturaSubheading"
            fallback={s.culturaSubheading || 'About Our Services'}
            className="mt-2 text-lg md:text-2xl font-bold uppercase tracking-widest"
            style={{ color: '#F4E400' }}
          />
        </div>

        {/* Carousel */}
        {loading ? (
          <div className="aspect-video w-full flex items-center justify-center bg-black/40 rounded-2xl">
            <Loader2 className="w-8 h-8 animate-spin text-white/60" />
          </div>
        ) : total === 0 ? (
          <div className="aspect-video w-full flex items-center justify-center bg-black/40 rounded-2xl text-white/60">
            No performances to display.
          </div>
        ) : (
          <div className="relative">
            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden bg-black">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.id || current.number}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0"
                >
                  <img
                    src={current.display_image || current.image}
                    alt={current.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-black/50" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 md:px-16">
                    <h4 className="text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight">
                      {current.number}- {current.title.toUpperCase()}
                    </h4>
                    <p className="mt-4 max-w-3xl text-sm md:text-base text-white/90 leading-relaxed line-clamp-4">
                      {(current.description || '').split('\n')[0]}
                    </p>
                    <Link
                      to={`/performances#performance-${current.number}`}
                      className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-md border-2 border-white text-white font-semibold text-sm hover:bg-white hover:text-black transition-colors"
                      data-testid={`perf-slide-click-${current.number}`}
                    >
                      Click Here
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Arrows */}
              <button
                onClick={prev}
                aria-label="Previous"
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
                data-testid="perf-slide-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={next}
                aria-label="Next"
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
                data-testid="perf-slide-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-4 flex-wrap">
              {items.map((it, i) => (
                <button
                  key={it.id || it.number}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === index ? 'bg-yellow-300' : 'bg-white/30 hover:bg-white/60'
                  }`}
                  data-testid={`perf-slide-dot-${i}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Body copy */}
        <div className="mt-10 max-w-4xl mx-auto space-y-4 text-white/90 text-sm md:text-base leading-relaxed">
          <Editable
            as="p"
            path="performancesSection.bodyParagraph1"
            fallback={s.bodyParagraph1 || ''}
            multiline
          />
          <Editable
            as="p"
            path="performancesSection.bodyParagraph2"
            fallback={s.bodyParagraph2 || ''}
            multiline
          />
        </div>
      </div>
    </section>
  );
};

export default HomePerformances;
